import { useRef } from "react";
import { resolveFontFamily } from "./fonts";
import { logoHeightVh } from "./logoSizes";
import {
  watermarkOpacity,
  watermarkSizePercent,
  watermarkVerticalPositionPercent,
} from "./watermarkDefaults";
import VenueClock from "./VenueClock";
import BlankLayout from "./BlankLayout";
import ScoreboardErrorBoundary from "./ScoreboardErrorBoundary";
import { DEFAULT_BACKGROUND_COLOR, DEFAULT_CLOCK_BAR_COLOR } from "./scoreboardDefaults";
// #1790 — this AC originally read brand/favicon/favicon.svg (the "only
// SQUARE Fox Village artwork" reasoning). That reasoning was already
// rejected once, for the exact same asset at the exact same blown-up scale,
// in blankLogoAssets.js's header (Jaybill, 2026-08-20): the favicon is
// "vestigial and NOT correct" and reads as a box/crate, not a brand mark,
// and "square" was never a real constraint. Using fox-village-mark-white.svg
// instead applies that same recorded decision here rather than re-making it.
import fvdMark from "brand/logo/fox-village-mark-white.svg";
import "./ScoreboardFrame.css";

// #1637 — the public board's shell (design §4.1, §8.4). Owns: theme
// application as CSS custom properties, logo slots, and the named EMPTY
// slots for SponsorStrip (#1639) and QRCorner (#1641) — this card does not
// implement either. The 3 layout families (Summary / Single entry / Ranked
// list) that fill `children` are #1652.
//
// #1692 — also owns the always-present venue clock, originally the
// bottom-right corner alongside the QR slot (#1641). #1790 moved the clock
// into the new full-width footer banner (left side) and dropped the
// staleness indicator this file used to also own outright, with no
// replacement liveness signal — the clock is now the only one. Placed HERE
// rather than in any one mode type/layout family so it is guaranteed on
// every one of them without touching #1652's per-family components, same
// reasoning as before the move. #1946 later moved the QR slot (#1641) into
// this same banner, on its right side alongside the brand mark — see the
// footer banner block below for why.

// #1798 — re-exported (see scoreboardDefaults.js's header for why the value
// itself lives in its own module, not here) so ScoreboardErrorBoundary's own
// self-contained fallback (which cannot rely on this component's CSS custom
// properties) uses the exact same color rather than a second hardcoded
// literal that could drift from this one.
export { DEFAULT_BACKGROUND_COLOR };
const DEFAULT_TEXT_COLOR = "#f5f5f7";
const DEFAULT_ACCENT_COLOR = "#f5a623";

function backgroundCssValue(background) {
  if (!background) return DEFAULT_BACKGROUND_COLOR;
  if (background.kind === "gradient" && background.colorBottom) {
    return `linear-gradient(to bottom, ${background.color}, ${background.colorBottom})`;
  }
  return background.color || DEFAULT_BACKGROUND_COLOR;
}

function themeCssVars(theme) {
  return {
    "--scoreboard-bg": backgroundCssValue(theme?.background),
    "--scoreboard-text-color": theme?.textColor || DEFAULT_TEXT_COLOR,
    "--scoreboard-accent-color": theme?.accentColor || DEFAULT_ACCENT_COLOR,
    "--scoreboard-font-family": resolveFontFamily(theme?.fontKey),
    // Card #2301 — same fallback shape as the three vars above: a
    // theme predating this field, or one that explicitly carries
    // clockBarColor: null, both resolve to the footer banner's own
    // pre-existing #147793 fox-blue appearance (DEFAULT_CLOCK_BAR_COLOR)
    // rather than an unset CSS custom property. This is the color actually
    // visible on a real board (the footer banner below), not the retired
    // VenueClock.css pill.
    "--scoreboard-clock-bar-color": theme?.clockBarColor || DEFAULT_CLOCK_BAR_COLOR,
  };
}

// Card #2321 HOTFIX, live show: superseded server-side fix. Prod measurement
// on this card recorded X-Amz-Credential varying across 9 of 12 polls
// roughly 1s apart for one UNCHANGED S3 key -- the short-date component of
// that field cannot change in 12 seconds, so the AKID itself changed:
// distinct warm Lambda containers hold distinct session credentials with no
// shared signing state, so no amount of server-side timestamp rounding can
// make two containers emit a byte-identical string. Jaybill's ruling
// (2026-09-05, lifting this card description's own original rejection of
// this exact approach): stabilise here instead, at the one place every
// presigned asset URL this frame renders actually lands.
//
// The S3 KEY (the path segment before "?") is the part that identifies the
// underlying object and is what the card's own prod measurement found
// IDENTICAL across all 12 polls even while the signature churned -- so
// comparing on that, not on the full URL string, is what tells "same image,
// fresh signature" (keep rendering the old string, no DOM change, no
// repaint) apart from "a genuinely different asset" (swap).
function s3KeyOf(url) {
  if (!url) return url;
  const queryIndex = url.indexOf("?");
  return queryIndex === -1 ? url : url.slice(0, queryIndex);
}

// One ref-backed Map per ScoreboardFrame instance (never module-scope state,
// so two boards -- or two renders of the theme editor's live-preview pane,
// #1640 -- never share entries), keyed by a caller-chosen stable id (the
// watermark, or a given logo slot). `stabilizeAssetUrl(id, url)` funnels
// EVERY presigned asset this frame renders through the same rule: return the
// incoming url the first time an id is seen or whenever its S3 key actually
// changes, otherwise return the PREVIOUS url for that id so an unchanged
// asset's re-signed string never reaches the DOM as something new.
function useAssetUrlStabilizer() {
  const cacheRef = useRef(new Map());
  return function stabilizeAssetUrl(id, url) {
    const cache = cacheRef.current;
    const key = s3KeyOf(url);
    const hit = cache.get(id);
    if (hit && hit.key === key) {
      return hit.url;
    }
    cache.set(id, { key, url });
    return url;
  };
}

export default function ScoreboardFrame({
  theme,
  isLoading = false,
  sponsorSlot = null,
  qrSlot = null,
  // "fixed" (default): the real public board, viewport-sized/chrome-free.
  // "embedded": card #1640's theme editor live-preview pane reuses this
  // same component with sample data (design §8.3) inside a small contained
  // box, not the actual browser viewport — this swaps position:fixed for
  // position:absolute (scoreboard-frame--embedded, ScoreboardFrame.css) so
  // it fills its own positioned ancestor instead of covering the screen.
  variant = "fixed",
  // #1798 AC2 — forwarded straight through to the content-level
  // ScoreboardErrorBoundary below. ScoreboardPage passes the same
  // `lastUpdated`-derived resetKey/logoAssetUrl (card #2308: the server-
  // resolved URL, not a hardcoded key) it gives its OWN outer boundary, so
  // both clear together on the next successful poll.
  resetKey = null,
  logoAssetUrl = null,
  onRenderStatus = null,
  children,
}) {
  const logos = Array.isArray(theme?.logos) ? theme.logos : [];
  const stabilizeAssetUrl = useAssetUrlStabilizer();
  const watermarkUrl = stabilizeAssetUrl("watermark", theme?.watermark?.url);
  const frameClassName =
    variant === "embedded" ? "scoreboard-frame scoreboard-frame--embedded" : "scoreboard-frame";

  return (
    <div className={frameClassName} style={themeCssVars(theme)} data-testid="scoreboard-frame">
      {/* #1943 — the venue watermark layer (design doc's own CSS block on
          the card): rendered FIRST so it paints under every other frame
          child in source order, at z-index 0 while .scoreboard-frame__content
          and .scoreboard-frame__footer-banner sit at z-index 1 (CSS). Gated
          on theme.watermark.url specifically (not just theme.watermark being
          set) so a theme with no watermark configured renders NO DOM node at
          all — AC3's "no empty layer" literally, not just an invisible one. */}
      {watermarkUrl ? (
        <div
          className="scoreboard-frame__watermark"
          data-testid="scoreboard-watermark"
          style={{
            // Quoted: an unquoted CSS url() cannot safely carry parens,
            // quotes, or whitespace, and a brand-asset presigned URL (query
            // string of arbitrary characters) is not guaranteed free of
            // them. #2321 hotfix: `watermarkUrl` is the STABILIZED value
            // (see useAssetUrlStabilizer above), not theme.watermark.url
            // directly -- a poll that only re-signs an unchanged S3 key
            // must never reach this backgroundImage as a new string.
            backgroundImage: `url("${watermarkUrl}")`,
            backgroundSize: `auto ${watermarkSizePercent(theme.watermark.size)}%`,
            backgroundPosition: `center ${watermarkVerticalPositionPercent(theme.watermark.verticalPosition)}%`,
            opacity: watermarkOpacity(theme.watermark.opacity),
          }}
        />
      ) : null}

      {logos.map((logo, index) => (
        <img
          key={`${logo.slot}-${index}`}
          // #2321 hotfix: same stabilizer as the watermark above, funneling
          // theme logos through the identical S3-key-comparison rule (the
          // card's own "fixes the watermark AND theme logos" intent, now
          // applied FE-side) -- id is per-slot-per-position so reordering
          // never cross-contaminates two logos' cached entries.
          src={stabilizeAssetUrl(`logo:${logo.slot}-${index}`, logo.url)}
          alt=""
          className={`scoreboard-frame__logo scoreboard-frame__logo--${logo.slot}`}
          style={{ height: `${logoHeightVh(logo.size)}vh` }}
          data-testid={`scoreboard-logo-${logo.slot}`}
        />
      ))}

      <div className="scoreboard-frame__content">
        {/* #1798 AC4 — before the first successful poll ever lands, and for
            as long as every poll keeps failing (a persistent network fault
            or a poll timeout, not just the first request), this used to be
            a "Loading board…" text state with no way out — an indefinite
            spinner if the first poll never succeeds at all. Blank+logo is
            the universal fallback here too now: correct at t=0 (nothing has
            loaded yet, which IS "nothing to show"), and correct forever if
            nothing ever loads, since it is a resting state, not a stuck one. */}
        {isLoading ? (
          <BlankLayout theme={theme} />
        ) : (
          // #1798 AC2 — a boundary around CONTENT ONLY, nested inside the
          // one ScoreboardPage.jsx already wraps around this whole frame.
          // A throw from `children` (ScoreboardContent, a layout family
          // component) is caught HERE first, so the frame chrome around
          // it — the logo slots above, the sponsor/QR slots and the footer
          // banner below — stays up exactly as #1692/#1788 intend: #1790's
          // removal of the staleness line left the clock (now in the
          // banner) as the audience's only liveness cue, and losing it on a
          // content-only fault would be an unasked-for behavior change this
          // card does not own. A
          // throw from sponsorSlot/qrSlot (built in ScoreboardPage, not
          // here) is NOT inside this boundary, so it still propagates to
          // ScoreboardPage's outer boundary and replaces the whole frame —
          // that is deliberate, not a gap: those two slots are siblings of
          // this content div, not descendants of it.
          <ScoreboardErrorBoundary
            resetKey={resetKey}
            logoAssetUrl={logoAssetUrl}
            theme={theme}
            onRenderStatus={onRenderStatus}
          >
            {children}
          </ScoreboardErrorBoundary>
        )}
      </div>

      {/* Named slot for the sponsor strip — card #1639 fills this in;
          deliberately empty here. */}
      <div className="scoreboard-frame__sponsor-slot" data-testid="scoreboard-sponsor-slot">
        {sponsorSlot}
      </div>

      {/* #1790 — full-width footer banner: clock (left) + QR/brand-mark
          group (right), white text/logo. Rendered as a sibling of
          .scoreboard-frame__content, OUTSIDE the isLoading ternary above,
          so it is frame-level and unconditional across every mode type
          including Blank and Message (AC11) — same placement pattern the
          venue clock/QR slot already used before this move. A normal
          (non-absolutely-positioned) flex child of .scoreboard-frame,
          so .scoreboard-frame__content's flex:1 yields it real vertical
          space rather than the banner overlaying content underneath.
          NOT the same element as Banner.jsx/Banner.css (card #1652's
          unrelated ranked-list banner, data-testid="banner") — distinct
          class/testid to avoid colliding with that existing component.
          Card #2301 — background used to be a hardcoded inline
          `colors.foxBlue`. This is the actual "clock bar" a venue sees
          (see themeCssVars above), so it now reads the theme's
          --scoreboard-clock-bar-color custom property, with an inline
          var() fallback to the exact same #147793 in case this element
          ever renders somewhere the property isn't set on an ancestor
          (belt-and-braces on top of themeCssVars always setting it). Text
          color stays hardcoded white regardless of theme (ScoreboardFrame.css
          `.scoreboard-frame__footer-banner`), so only background is themed
          here. */}
      <div
        className="scoreboard-frame__footer-banner"
        data-testid="scoreboard-footer-banner"
        style={{ backgroundColor: "var(--scoreboard-clock-bar-color, #147793)" }}
      >
        <VenueClock />
        {/* #1946 — the QR (#1641) used to float above this banner at
            bottom-right (.scoreboard-frame__bottom-right, since removed),
            which collided with the last ranked-list row once a division
            owned the full board width. It now lives inside the banner,
            next to the brand mark, so the content area (flex:1 above this
            banner) is clear top to bottom on every mode type — the same
            guarantee that already protected the clock. QR content, module
            size and quiet zone are untouched (QRCorner.jsx/.css, still
            13vh); only its parent changed. `.scoreboard-frame__qr-slot:empty`
            (CSS) collapses this slot to zero width when no QR is configured,
            so an unconfigured board leaves no gap before the brand mark. */}
        <div className="scoreboard-frame__footer-right">
          {/* #2270 AC7 (Jaybill, 2026-09-04, matching demo pages p4/p5) — a
              bare code tells an audience nothing about what scanning it
              gets them, and the QR is the only thing on the board that
              leads anywhere. Rendered as a sibling of the qr-slot (not
              inside it) so it tracks the QR itself: gated on `qrSlot`
              being present, the same condition the slot's own `:empty`
              CSS rule uses, so an unconfigured board's caption disappears
              along with the code rather than captioning empty space. */}
          {qrSlot ? (
            <span className="scoreboard-frame__qr-label" data-testid="scoreboard-qr-label">
              Scan for results
            </span>
          ) : null}
          <div className="scoreboard-frame__qr-slot" data-testid="scoreboard-qr-slot">
            {qrSlot}
          </div>
          <img src={fvdMark} alt="" className="scoreboard-frame__footer-banner-logo" />
        </div>
      </div>
    </div>
  );
}
